import { useCallback, useState, useContext, useRef } from 'react';

import routes from 'sitewide-js/core_pre_webpack/routes';
import { showBulkDownloadModal } from 'js/bulk_download/show_bulk_download_modal';
import {
  bulkDownloadImpressionSource,
  BulkDownloadTrigger,
} from 'js/bulk_download/bulk_download_helper';
import { useToast } from 'js/design_system/toasts/hooks';
import { currentUser } from 'js/utils/user';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { fsEvent } from 'js/fullstory';

import { ReaderMessageContext } from 'js/liswp/work_actions/ReaderMessageContext';
import { useDownloadTracking } from 'js/liswp/work_actions/useDownloadTracking';
import { Work, WorkOwnerType } from 'js/liswp/types';
import { recordClickTimestamp } from 'js/bulk_download/bulk_download_timing';

function isSystemOwned(ownerType?: WorkOwnerType): boolean {
  return ownerType === 'OA_IMPORTED' || ownerType === 'PUBLISHING_OWNED';
}

function isAuthoredByCurrentUser(work: Work): boolean {
  return Boolean(
    currentUser && work.authors.some((author) => author.id === currentUser?.id),
  );
}

async function addToLibrary(
  workId: string | number,
  contentType: string,
  source: string,
): Promise<Response> {
  let fromDownloadAt = null;
  if (source === 'download') {
    fromDownloadAt = new Date();
  }

  return fetchWithCsrf(
    routes.api_v0_bookmarks_path({
      'bookmark[content_id]': workId,
      'bookmark[source]': source,
      'bookmark[from_download_at]': fromDownloadAt,
      'bookmark[content_type]': contentType,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
    },
  );
}

export interface UseDownloadOptions {
  work: Work;
  source: string;
  premium: boolean;
  gateDownload?: boolean;
  shouldAutoBookmark?: boolean;
  showAutoBookmarkToast?: boolean;
  showReaderMessageModal?: boolean;
  showBulkDownloadUpsell?: boolean;
  showLookbackUpsell?: boolean;
  isAutoDownload?: boolean;
  useDownloadAttribute?: boolean;
  bulkDownloadPurchased?: boolean;
  trialPricingCopy?: string;
  /** Called to perform the actual download instead of clicking the hidden anchor.
   * Use this for external (landing page) downloads that open a new window. */
  onDownload?: () => void;
}

interface DownloadAnchorProps {
  ref: React.RefObject<HTMLAnchorElement>;
  href: string;
  target: string;
  download: true | undefined;
  style: React.CSSProperties;
}

interface LookbackUpsellState {
  open: boolean;
  close: () => void;
  workId: string | number;
  impressionSource: string;
  trialPricingCopy?: string;
}

interface ReaderMessageModalState {
  open: boolean;
  close: () => void;
  primaryAuthor: Work['authors'][0];
  workId: string | number;
  attachmentId: number;
  source: string;
}

export interface UseDownloadResult {
  /** Click handler for download buttons — calls preventDefault + startDownload */
  onDownloadClick: (e: React.MouseEvent) => void;
  /** Imperative trigger (for auto-download, programmatic use) */
  startDownload: () => void;
  /** Props to spread on a hidden <a> for triggering the actual download */
  downloadAnchorProps: DownloadAnchorProps;
  /** Reader message modal state — render ReaderMessageModal when open is true */
  readerMessageModal: ReaderMessageModalState;
  /** Lookback upsell modal state — render FallbackBulkDownloadUpsellModalControl when open */
  lookbackUpsell: LookbackUpsellState;
}

export function useDownload(options: UseDownloadOptions): UseDownloadResult {
  const {
    work,
    source,
    premium = false,
    gateDownload = false,
    shouldAutoBookmark = false,
    showAutoBookmarkToast = true,
    showReaderMessageModal = true,
    showBulkDownloadUpsell = false,
    showLookbackUpsell = false,
    isAutoDownload = false,
    useDownloadAttribute = false,
    bulkDownloadPurchased = false,
    trialPricingCopy,
    onDownload,
  } = options;

  const context = useContext(ReaderMessageContext);
  const { infoToast } = useToast();

  const downloadAnchor = useRef<HTMLAnchorElement>(null);
  const [lookbackUpsellShown, setLookbackUpsellShown] = useState(false);
  const [readerMessageModalOpen, setReaderMessageModalOpen] = useState(false);

  const attachment = work.downloadableAttachment;

  const downloadUrlWithTrackingToken = useDownloadTracking(
    attachment?.downloadUrl || '',
    source,
  );

  const autoBookmark = useCallback(async () => {
    await addToLibrary(
      work.id,
      typeof work.id === 'string' ? 'OpenalexWork' : 'Work',
      'download',
    );

    if (showAutoBookmarkToast) {
      infoToast('Bookmarked');
    }
  }, [infoToast, showAutoBookmarkToast, work]);

  const downloadWork = useCallback(() => {
    if (onDownload) {
      onDownload();
    } else if (downloadAnchor.current) {
      downloadAnchor.current.click();
    }

    const systemOwned = isSystemOwned(work.ownerType);

    if (
      shouldAutoBookmark &&
      !work.isBookmarked &&
      !systemOwned &&
      !isAuthoredByCurrentUser(work) &&
      currentUser?.preferences?.public_bookmarks
    ) {
      autoBookmark();
    }

    const readerMessageLimitReached = context?.readerMessageLimitReached;
    const primaryAuthor = work.authors[0];

    if (
      showReaderMessageModal &&
      !systemOwned &&
      primaryAuthor &&
      !isAuthoredByCurrentUser(work) &&
      !readerMessageLimitReached
    ) {
      setReaderMessageModalOpen(true);
    }

    if (
      source === 'swp-bulk-download-modal' ||
      source === 'swp-sticky-header' ||
      source === 'swp-toolbar'
    ) {
      fetch(
        routes.api_v0_post_download_coverage_path({
          subdomain_param: 'api',
          work_id: work.id,
        }),
        {},
      );
    }
  }, [
    autoBookmark,
    context?.readerMessageLimitReached,
    onDownload,
    shouldAutoBookmark,
    showReaderMessageModal,
    source,
    work,
  ]);

  const renderBulkDownloadModal = useCallback(() => {
    showBulkDownloadModal({
      trigger: `${source}-work-card-download-button` as BulkDownloadTrigger,
      gatedDownload: gateDownload,
      work: {
        id: work.id,
        slug: work.slug,
        title: work.title,
        attachment: {
          url: attachment?.downloadUrl ?? '',
          id: attachment?.id ?? 0,
        },
        authors: work.authors,
        doi: work.doi,
        licenseAllowsAttachment: work.licenseAllowsAttachment,
      },
      purchased: bulkDownloadPurchased,
      readerMessageLimitReached: context?.readerMessageLimitReached,
      setReaderMessageLimitReached: context?.setReaderMessageLimitReached,
      landingPageUrl: work.landingPageUrl,
    });
  }, [attachment, context, bulkDownloadPurchased, gateDownload, source, work]);

  const startDownload = useCallback(() => {
    if (premium || bulkDownloadPurchased) {
      downloadWork();
    } else if (showBulkDownloadUpsell) {
      if (!gateDownload) {
        downloadWork();
        fsEvent('ds_liswp.bulk_download_upsell', {
          dsBucket: window.cwvAbTestBucket,
        });
      } else {
        fsEvent('ds_liswp.bulk_download_upsell.gated_download', {
          dsBucket: window.cwvAbTestBucket,
        });
      }
      recordClickTimestamp(work.id);
      renderBulkDownloadModal();
    } else if (
      showLookbackUpsell &&
      !window.matchMedia('(max-width: 767px)').matches
    ) {
      fsEvent('ds_liswp.bulk_download_lookback_upsell', {
        dsBucket: window.cwvAbTestBucket,
      });
      setLookbackUpsellShown(true);
    } else {
      downloadWork();
    }
  }, [
    bulkDownloadPurchased,
    showLookbackUpsell,
    downloadWork,
    gateDownload,
    premium,
    renderBulkDownloadModal,
    showBulkDownloadUpsell,
    work,
  ]);

  const onDownloadClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      startDownload();
    },
    [startDownload],
  );

  const isFirefox =
    typeof window !== 'undefined' && navigator.userAgent.includes('Firefox');
  const target = isFirefox && !isAutoDownload ? '_blank' : '';

  const primaryAuthor = work.authors[0];
  const normalizedSource = source === 'news-feed' ? 'news' : source;

  const closeLookbackUpsell = useCallback(() => {
    setLookbackUpsellShown(false);
    window.Aedu.Dismissibles.recordShow('BulkDownloadLookbackUpsellModal');
    downloadWork();
  }, [downloadWork]);

  const impressionSource = bulkDownloadImpressionSource(
    'upsell',
    `${source}-work-card-download-button` as BulkDownloadTrigger,
  );

  return {
    onDownloadClick,
    startDownload,
    downloadAnchorProps: {
      ref: downloadAnchor,
      href: downloadUrlWithTrackingToken,
      target,
      download: useDownloadAttribute ? true : undefined,
      style: { visibility: 'hidden' as const, display: 'none' },
    },
    readerMessageModal: {
      open: readerMessageModalOpen && !!primaryAuthor,
      close: () => setReaderMessageModalOpen(false),
      primaryAuthor,
      workId: work.id,
      attachmentId: attachment?.id ?? 0,
      source: normalizedSource,
    },
    lookbackUpsell: {
      open: lookbackUpsellShown,
      close: closeLookbackUpsell,
      workId: work.id,
      impressionSource,
      trialPricingCopy,
    },
  };
}
